import React, { FC, useCallback, useEffect } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { useFindCarrierByDotLazyQuery } from '../../graphql/FindCarrierByDOT'
import { useFindCarrierByMcLazyQuery } from '../../graphql/FindCarrierByMC'
import { getErrorMessage } from '../../schema'

export interface IValidatedCarrierNumberInputProps
  extends Pick<UseFormMethods, 'control' | 'errors' | 'trigger' | 'setError' | 'clearErrors'> {
  name: 'mcNumber' | 'dotNumber'
}

const ValidatedCarrierNumberInput: FC<IValidatedCarrierNumberInputProps> = ({
  clearErrors,
  control,
  errors,
  name,
  setError,
  trigger,
}) => {
  const label = name === 'mcNumber' ? 'MC Number' : 'DOT Number'

  const value = useWatch<string>({ control, name })

  const addUnavailableError = useCallback(() => {
    setError(name, {
      message: `${label} is already associated with a carrier.`,
      type: 'number-unavailable',
      shouldFocus: true,
    })
  }, [label, name, setError])

  const handleFormErrors = useCallback(
    (isCarrierExisting: boolean) => {
      if (isCarrierExisting) {
        addUnavailableError()
      } else {
        clearErrors(name)
      }
    },
    [addUnavailableError, clearErrors, name]
  )

  const [findByMC] = useFindCarrierByMcLazyQuery({
    onCompleted: data => {
      const { findCarrierApplicationByMcNumber: carrierWithMC } = data || {}
      handleFormErrors(Boolean(carrierWithMC))
    },
  })
  const [findByDOT] = useFindCarrierByDotLazyQuery({
    onCompleted: data => {
      const { findCarrierApplicationByDotNumber: carrierWithDOT } = data || {}
      handleFormErrors(Boolean(carrierWithDOT))
    },
  })

  const triggerValidation = useCallback(async () => {
    // triggers yup validation
    const isValid = await trigger(name)
    if (isValid) {
      if (name === 'mcNumber') {
        findByMC({ variables: { mcNumber: value || '' } })
      } else {
        findByDOT({ variables: { dotNumber: value || '' } })
      }
    }
  }, [findByDOT, findByMC, name, trigger, value])

  /**
   * This useEffect is triggered anytime value changes.
   * If there is a value, the form will validate
   * If not, errors will clear (note: if the field is required an error will appear)
   */
  useEffect(() => {
    if (value) {
      triggerValidation()
    } else {
      clearErrors(name)
    }
  }, [clearErrors, name, triggerValidation, value])

  return (
    <ControlledTextInput
      control={control}
      name={name}
      TextInputProps={{
        label,
        error: Boolean(getErrorMessage(errors, name)),
        validationMessage: getErrorMessage(errors, name),
        type: 'number',
        required: name === 'dotNumber',
        dataTest: `${name}-validated-input`,
      }}
    />
  )
}

export default ValidatedCarrierNumberInput
